import React from "react";
import SummaryCardLayout from "./SummaryCardLayout";
import { getFullName } from "../../../../Modules/utils/fullname";
import { RenderDenomination } from "../../../../Components/etc/RenderAmount";

export default function FinalSummaryUserPayIn(props) {
  const { data } = props;

  // ----------------------------------------------------
  // Basic Extracts
  // ----------------------------------------------------
  const cash = Number(data.totalAmountDetails?.cashAmount || 0);
  const online = Number(data.totalAmountDetails?.onlineAmount || 0);
  const adj = Number(data.totalAmountDetails?.adjustedAmount || 0);
  const total = Number(data.totalAmount || 0);

  const rtoLossAmount = (data.rtoLossList || []).reduce(
    (s, r) => s + Number(r.amount),
    0
  );

  const INR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  // ----------------------------------------------------
  // Preview layout (no print styling)
  // ----------------------------------------------------
  const renderPreviewContent = (d) => (
    <div className="p-0">
      <table className="table table-sm mb-2">
        <tbody>
          <tr>
            <td>Amount Received From</td>
            <td className="text-end fw-bold">{getFullName(d.agent)}</td>
          </tr>
        </tbody>
      </table>

      <div className="fw-semibold small mt-3">Amount Summary</div>
      <table className="table table-sm mb-2">
        <tbody>
          {rtoLossAmount ? (
            <tr>
              <td className="fw-bold">Total RTO Loss Amount</td>
              <td className="text-end fw-bold">{INR(rtoLossAmount)}</td>
            </tr>
          ) : null}
          <tr>
            <td>Cash Paid</td>
            <td className="text-end">{INR(cash)}</td>
          </tr>
          {data.totalAmountDetails.cashDenominations ? (
            <tr>
              <td className="text-muted">Denominations</td>
              <td className="text-end text-muted small">
                <RenderDenomination
                  denominations={data.totalAmountDetails.cashDenominations}
                />
              </td>
            </tr>
          ) : null}
          <tr>
            <td>Online Paid</td>
            <td className="text-end">{INR(online)}</td>
          </tr>
          {adj !== 0 && (
            <tr>
              <td>Adjustment</td>
              <td className="text-end text-danger">{INR(adj)}</td>
            </tr>
          )}
          <tr>
            <td className="fw-bold">Total Amount</td>
            <td className="text-end fw-bold">{INR(total)}</td>
          </tr>
        </tbody>
      </table>

      {data.rtoLossList?.length ? (
        <>
          <h6 className="fw-bold mb-2">RTO Loss List</h6>

          <table className="table table-borderless table-sm mb-3">
            <tbody>
              <tr>
                <th>ID</th>
                <th>Amount</th>
                <th>Note</th>
              </tr>
              {data.rtoLossList?.map((x, i) => (
                <tr key={i}>
                  <td>{x.id}</td>
                  <td>{x.amount}</td>
                  <td>{x.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      {d.note && (
        <>
          <div className="fw-semibold small mt-2">Note</div>
          <div className="border rounded p-2 small">{d.note}</div>
        </>
      )}
    </div>
  );

  // ----------------------------------------------------
  // Printable summary
  // ----------------------------------------------------
  const renderPrintableContent = (d) => (
    <div className="p-0">
      <h6 className="fw-bold mb-2">Deposit Summary</h6>

      <table className="table table-borderless table-sm mb-3">
        <tbody>
          <tr>
            <td>Amount Received From</td>
            <td className="text-end fw-bold">{getFullName(d.relatedUser)}</td>
          </tr>
          {rtoLossAmount ? (
            <tr>
              <td className="fw-bold">Total RTO Loss Amount</td>
              <td className="text-end fw-bold">{INR(rtoLossAmount)}</td>
            </tr>
          ) : null}
          <tr>
            <td>Cash Deposit</td>
            <td className="text-end">{INR(cash)}</td>
          </tr>
          {data.totalAmountDetails.cashDenominations ? (
            <tr>
              <td className="text-muted">Denominations</td>
              <td className="text-end text-muted small">
                <RenderDenomination
                  denominations={data.totalAmountDetails.cashDenominations}
                />
              </td>
            </tr>
          ) : null}
          <tr>
            <td>Online Deposit</td>
            <td className="text-end">{INR(online)}</td>
          </tr>
          {adj !== 0 && (
            <tr>
              <td>Adjustment</td>
              <td className="text-end">{INR(adj)}</td>
            </tr>
          )}

          <tr className="table-light">
            <td colSpan={2}>
              <small className="text-muted">Total = Cash + Online</small>
            </td>
          </tr>

          <tr>
            <td className="fw-bold">Total Amount</td>
            <td className="text-end fw-bold">{INR(total)}</td>
          </tr>
        </tbody>
      </table>

      {data.rtoLossList?.length ? (
        <>
          <h6 className="fw-bold mb-2">RTO Loss List</h6>

          <table className="table table-borderless table-sm mb-3">
            <tbody>
              <tr>
                <th>ID</th>
                <th>Amount</th>
                <th>Note</th>
              </tr>
              {data.rtoLossList?.map((x, i) => (
                <tr key={i}>
                  <td>{x.id}</td>
                  <td>{x.amount}</td>
                  <td>{x.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}

      <h6 className="fw-bold mt-3">Total Balance</h6>
      <table className="table table-sm mb-2">
        <tbody>
          <tr>
            <td>User Balance Due (total)</td>
            <td className="text-end fw-bold">
              {INR(Number(data.userBalanceDue?.total))}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  // ----------------------------------------------------
  // Determine mode
  // ----------------------------------------------------
  let mode = "preview";
  if (props.mode === "details") mode = "details";
  else if (data?.transaction?._id) mode = "submitted";

  // ----------------------------------------------------
  // Render using SummaryCardLayout wrapper
  // ----------------------------------------------------
  return (
    <SummaryCardLayout
      mode={mode}
      data={data}
      renderPreviewContent={renderPreviewContent}
      renderPrintableContent={renderPrintableContent}
      onBack={() => props.onSubmit({ step: data.step - 1 })}
      onSubmit={props.onFinalSubmit}
      onClose={props.handleClose}
      onEdit={props.handleEdit}
      onDelete={props.handleDelete}
      onApprove={props.handleApprove}
    />
  );
}
